<template>
  <div ref="tabsRoot" class="video-guest-tabs" data-testid="video-guest-tabs">
    <Tabs id="video-guest-tabs" :selected-tab="selectedTab" @tab-click="onTabClick" @changed="(e: any) => onTabChange(e.tab.hash)">
      <!-- Summary Tab (default) -->
      <Tab id="summary" :hidden="!hasSummary" name="Summary" class="video-guest-tabs__tab">
        <div class="video-guest-tabs__summary">
          <SummaryContent />
        </div>
      </Tab>

      <!-- Comments Tab -->
      <Tab id="comments" :hidden="!commentsEnabled" name="Comments" class="video-guest-tabs__tab">
        <div class="video-guest-tabs__comments">
          <Comments @addedComment="emit('addedComment')" />
        </div>
      </Tab>

      <!-- Transcript Tab -->
      <Tab id="transcript" :hidden="!transcriptionTabEnabled" name="Transcript" class="video-guest-tabs__tab">
        <Transcription />
      </Tab>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Tab, Tabs } from '@/storybook';

import Comments from '@/modules/viewer/components/item_tabs/comments/Comments.vue';
import Transcription from '@/modules/viewer/components/item_tabs/transcription/Transcription.vue';
import SummaryContent from '@/modules/viewer/components/SummaryContent.vue';

import { useViewerItemStore, ItemType } from '@/modules/viewer/store/useViewerItemStore';
import { useTranscriptionStore, ItemTranscriptionStatus } from '@/modules/viewer/store/useTranscriptionStore';
import { useTabNavigation } from '@/modules/viewer/composables/useTabNavigation';
import { useActiveTabListener } from '@/modules/viewer/composables/useActiveTab';

const emit = defineEmits<{
  addedComment: [];
}>();

const store = useViewerItemStore();
const transcriptionStore = useTranscriptionStore();
const tabsRoot = ref<HTMLElement | null>(null);

const { type, commentsEnabled, description } = storeToRefs(store);
const { transcriptionStatus } = storeToRefs(transcriptionStore);

const hasSummary = computed(() => !!description.value && description.value.length > 0);

const transcriptionTabEnabled = computed(() => {
  return (
    type.value === ItemType.VIDEO &&
    transcriptionStatus.value &&
    [ItemTranscriptionStatus.DONE, ItemTranscriptionStatus.EDITED].includes(transcriptionStatus.value)
  );
});

const availableTabs = computed(() => {
  const tabs: string[] = [];
  if (hasSummary.value) {
    tabs.push('#summary');
  }
  if (commentsEnabled.value) {
    tabs.push('#comments');
  }
  if (transcriptionTabEnabled.value) {
    tabs.push('#transcript');
  }
  return tabs;
});

const { selectedTab, onTabClick, onTabChange, selectTab } = useTabNavigation(availableTabs, tabsRoot, '#summary');

// Listen for external tab switch requests (from EngagementBar)
const { activeTabOverride } = useActiveTabListener();
watch(activeTabOverride, tab => {
  if (tab) {
    selectTab(tab);
    activeTabOverride.value = null;
  }
});
</script>

<style scoped>
.video-guest-tabs {
  @apply mt-4;
}

.video-guest-tabs__tab {
  @apply mt-3 sm:mt-4;
  min-height: 200px;
}

@media (min-width: 640px) {
  .video-guest-tabs__tab {
    min-height: 300px;
  }
}

.video-guest-tabs__summary {
  @apply relative flex flex-col p-3 sm:p-4 sm:border sm:border-slate-300 sm:dark:border-zinc-700 bg-white dark:bg-zinc-900 sm:rounded-10;
}

.video-guest-tabs__comments {
  @apply relative flex flex-col p-3 sm:p-4 sm:border sm:border-slate-300 sm:dark:border-zinc-700 bg-white dark:bg-zinc-900 sm:rounded-10;
}
</style>
